import {
  collection,
  deleteDoc,
  doc,
  DocumentData,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  WithFieldValue,
  PartialWithFieldValue,
  query,
  QueryConstraint,
  where,
  WhereFilterOp,
  FirestoreError,
  setDoc
} from "firebase/firestore";
import { db } from "../firebase";
import { Collection } from "../enums/collections.enum";

// Type definitions for better type safety
export interface CrudResult<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface FilterCondition {
  field: string;
  operator: WhereFilterOp;
  value: any;
}

export const createEntity = async <T extends DocumentData>(
  collectionName: Collection,
  data: T,
  id?: string
): Promise<T> => {
  console.log(`[crudService] createEntity called - Collection: ${collectionName}`);
  
  try {
    // Validate inputs
    if (!collectionName) {
      throw new Error('Collection name is required');
    }

    if (!data || typeof data !== 'object') {
      throw new Error('Invalid document data');
    }

    // Create a copy and remove id field if present
    const dataCopy = { ...data } as any;
    delete dataCopy.id;

    let documentId: string;

    // Generate new document
    let docRef;
    if (id) {
      // Use existing ID
      docRef = doc(db, collectionName, id);
      await setDoc(docRef, dataCopy);
      documentId = id;
    } else {
      const collectionRef = collection(db, collectionName);
      docRef = await addDoc(collectionRef, dataCopy);
    }

    if (!docRef?.id) {
      throw new Error('Firestore did not return a document ID');
    }

    documentId = docRef.id;

    console.log(`[crudService] createEntity response - Document created in ${collectionName} with ID: ${documentId}`);
    return { id: docRef.id, ...dataCopy } as T;

  } catch (error) {
    const firestoreError = error as FirestoreError;
    const errorMessage = `Failed to create document in ${collectionName}: ${firestoreError.message}`;
    
    console.error('Firestore operation failed:', {
      code: firestoreError.code,
      message: firestoreError.message,
      collection: collectionName,
      data: JSON.stringify(data, null, 2)
    });

    throw new Error(errorMessage);
  }
};

export const readEntity = async <T extends DocumentData>(
  collectionName: Collection,
  id: string
): Promise<T | undefined> => {
  console.log(`[crudService] readEntity called - Collection: ${collectionName}, ID: ${id}`);
  
  try {
    if (!id) {
      console.warn('[crudService] readEntity - No ID provided');
      return undefined;
    }

    const docRef = doc(db, collectionName, id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = { id: docSnap.id, ...docSnap.data() } as unknown as T;
      console.log(`[crudService] readEntity response - Document found in ${collectionName}:`, data);
      return data;
    } else {
      console.log(`[crudService] readEntity response - No document found in ${collectionName} with ID: ${id}`);
      return undefined;
    }
  } catch (error) {
    console.error("Error reading document: ", error);
    throw error; // Re-throw to allow caller to handle
  }
};

export const updateEntity = async <T extends DocumentData>(
  collectionName: Collection,
  id: string,
  data: PartialWithFieldValue<T>
): Promise<void> => {
  console.log(`[crudService] updateEntity called - Collection: ${collectionName}, ID: ${id}`);
  
  try {
    if (!id) {
      throw new Error('Document ID is required for update');
    }

    const docRef = doc(db, collectionName, id);
    await updateDoc(docRef, data as WithFieldValue<T>);
    
    console.log(`[crudService] updateEntity response - Document successfully updated in ${collectionName} with ID: ${id}`);
  } catch (error) {
    console.error("Error updating document: ", error);
    throw error;
  }
};

export const deleteEntity = async (
  collectionName: Collection,
  id: string
): Promise<void> => {
  console.log(`[crudService] deleteEntity called - Collection: ${collectionName}, ID: ${id}`);
  
  try {
    if (!id) {
      throw new Error('Document ID is required for delete');
    }

    const docRef = doc(db, collectionName, id);
    await deleteDoc(docRef);
    
    console.log(`[crudService] deleteEntity response - Document successfully deleted from ${collectionName} with ID: ${id}`);
  } catch (error) {
    console.error("Error deleting document: ", error);
    throw error;
  }
};

export const readAllEntity = async <T extends DocumentData>(
  collectionName: Collection
): Promise<T[]> => {
  console.log(`[crudService] readAllEntity called - Collection: ${collectionName}`);
  
  try {
    if (!collectionName) {
      throw new Error('Collection name is required');
    }

    const collectionRef = collection(db, collectionName);
    const snapshot = await getDocs(collectionRef);

    const dataList = snapshot.docs.map((doc) => {
      return {
        id: doc.id,
        ...doc.data(),
      } as unknown as T;
    });

    console.log(`[crudService] readAllEntity response - Retrieved ${dataList.length} documents from ${collectionName}`);
    return dataList;
  } catch (error) {
    console.error("Error reading documents: ", error);
    throw error;
  }
};

export const readFilteredEntity = async <T extends DocumentData>(
  collectionName: Collection,
  filters: FilterCondition[]
): Promise<T[]> => {
  console.log(`[crudService] readFilteredEntity called - Collection: ${collectionName}, Filters:`, filters);
  
  try {
    if (!collectionName) {
      throw new Error('Collection name is required');
    }

    if (!filters || !Array.isArray(filters)) {
      throw new Error('Filters must be an array');
    }

    const collectionRef = collection(db, collectionName);
    const firebaseFilters: QueryConstraint[] = filters.map((filter) =>
      where(filter.field, filter.operator, filter.value)
    );

    const q = query(collectionRef, ...firebaseFilters);
    const snapshot = await getDocs(q);

    const dataList = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as unknown as T[];

    console.log(`[crudService] readFilteredEntity response - Retrieved ${dataList.length} filtered documents from ${collectionName}`);
    return dataList;
  } catch (error) {
    if (error instanceof FirestoreError) {
      if (error.code === 'failed-precondition') {
        console.error(
          "This query requires an index. Please create the necessary index in the Firebase Console.",
          error.message
        );
      } else {
        console.error("Error reading filtered documents: ", error);
      }
    } else {
      console.error("Unexpected error:", error);
    }
    throw error;
  }
};
